import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * FE-FILTER02: фильтры, страница и сортировка живут в query-string, чтобы
 * Back из карточки и reload возвращали тот же список. Пишем только значения,
 * отличные от default, чтобы URL оставался коротким; чужие параметры
 * (tab, ruleId) не трогаем. По умолчанию replace — набор текста в фильтре
 * не должен плодить историю.
 */
const coerce = (raw, spec) => {
  if (raw == null) return spec.default;
  if (spec.type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : spec.default;
  }
  if (spec.type === 'boolean') return raw === 'true';
  return raw;
};

export const useUrlState = (schema) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result = {};
    Object.entries(schema).forEach(([key, spec]) => {
      result[key] = coerce(searchParams.get(spec.param), spec);
    });
    return result;
  }, [schema, searchParams]);

  const setValues = useCallback((partial, { replace = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(partial).forEach(([key, value]) => {
      const spec = schema[key];
      if (!spec) return;
      // Дефолт — это именно spec.default, а не любая falsy-строка: ChangeRequestsTab
      // хранит default:'pending', и явный выбор '' («Все статусы») должен уйти в
      // URL как параметр с пустым значением, а не молча схлопнуться в дефолт.
      const isDefault = value == null || value === spec.default;
      if (isDefault) next.delete(spec.param);
      else next.set(spec.param, String(value));
    });
    setSearchParams(next, { replace });
  }, [schema, searchParams, setSearchParams]);

  return [values, setValues];
};

export const HIERARCHY_URL_SCHEMA = {
  factoryId: { param: 'factory', default: '' },
  workshopId: { param: 'workshop', default: '' },
  sectionId: { param: 'section', default: '' },
  roomId: { param: 'room', default: '' },
  cameraId: { param: 'camera', default: '' },
};

export const pickHierarchy = (values) => ({
  factoryId: values.factoryId || '',
  workshopId: values.workshopId || '',
  sectionId: values.sectionId || '',
  roomId: values.roomId || '',
  cameraId: values.cameraId || '',
});

/**
 * FE-FILTER02: «применённые» фильтры (из URL) и черновик формы — разные вещи.
 * `url`, который отдаёт useUrlState, пересоздаётся при ЛЮБОМ изменении query
 * (в т.ч. limit/offset и чужих параметров вроде ruleId, потому что
 * useSearchParams общий на страницу). Если синхронизировать draft эффектом
 * прямо на `url`, черновик будет незаметно затираться пагинацией или чужими
 * параметрами. pick(url) должен возвращать только поля самого фильтра (без
 * limit/offset) — тогда identity appliedFilters меняется только тогда, когда
 * реально изменились значения фильтра, и draft переживает клики "Вперёд"/"Назад".
 */
export const useAppliedFilters = (url, pick) => {
  const key = JSON.stringify(pick(url));
  // pick(url) детерминирован по key — url и pick намеренно не в deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appliedFilters = useMemo(() => pick(url), [key]);

  const [draftFilters, setDraftFilters] = useState(() => appliedFilters);
  // Back/Forward меняет URL напрямую, минуя draft — синхронизируем черновик,
  // но только когда изменились сами поля фильтра, а не что-то постороннее.
  useEffect(() => { setDraftFilters(appliedFilters); }, [appliedFilters]);

  return [appliedFilters, draftFilters, setDraftFilters];
};
